import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { Input } from '@foam/buoy/src/components/Input/Input';
import { Modal } from '@foam/buoy/src/components/Modal/Modal';
import { Popover } from '@foam/buoy/src/components/Popover/Popover';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { BrandfetchBrand } from '~/api/brandfetch';
import { useBrandfetchSearch } from '~/hooks/api/useBrandfetchSearch';
import {
  AvailableLogos,
  SearchDropdown,
  SelectedBrandLogos,
  StaticBrandLogos,
  useBrandExperienceModalStore,
} from '~/screens/PageBuilder/modals/BrandExperienceModal';

import { usePageBuilderStore } from '../../hooks/usePageBuilderStore';
import { AnyBlock, Block } from '../../types';

// Interface for individual logo items
export interface BrandLogoItem {
  id: string;
  url: string;
  brandName: string;
}

// Interface for selected logos (block properties)
export interface BrandLogosProperties {
  logos: BrandLogoItem[];
}

export const BrandExperienceModal = () => {
  const { blocks } = usePageBuilderStore();
  // this custom store in an artifact of pre-ui-library code. this could be replaced with 1 "useModalStore" instance if needed.
  // however, when the feature complexity requires a custom zustand store, it's totally fine to manually control Modal
  const { isOpen, reset, callback } = useBrandExperienceModalStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [logos, setLogos] = useState<BrandLogoItem[]>([]);
  const [selectedBrandForLogos, setSelectedBrandForLogos] =
    useState<BrandfetchBrand | null>(null);

  const { brands, isLoading } = useBrandfetchSearch({
    searchTerm,
    enabled: isOpen,
    minLength: 2,
  });

  const brandLogosBlock = blocks.find(
    (block: AnyBlock) => block.component === 'BrandLogos',
  ) as Block<'BrandLogos'> | undefined;

  useEffect(() => {
    return () => {
      // Reset the modal when it unmounts
      reset();
      setLogos([]);
    };
  }, [reset]);

  // Set existing brand logos when modal opens
  useEffect(() => {
    if (isOpen && brandLogosBlock?.properties.logos) {
      setLogos(brandLogosBlock.properties.logos);
    } else if (isOpen) {
      setLogos([]);
    }
  }, [isOpen]);

  const closeModal = useCallback(() => {
    reset();
    setLogos([]);
    setSelectedBrandForLogos(null);
  }, [reset]);

  const handleSave = useCallback(() => {
    callback({ logos });
    closeModal();
  }, [callback, logos, closeModal]);

  const handleResetSearch = useCallback(() => {
    setSearchTerm('');
    setSelectedBrandForLogos(null);
  }, [setSearchTerm]);

  const handleBrandSelect = useCallback(
    (logo: string, name: string) => {
      handleLogoSelect(logo, name);
      setSearchTerm('');
    },
    [setSearchTerm],
  );

  const handleLogoSelect = useCallback(
    (url: string, brandName: string) => {
      setLogos((prevLogos) => {
        // Check if logo already exists
        const exists = prevLogos.some(
          (logo) => logo.url === url && logo.brandName === brandName,
        );
        if (exists) {
          return prevLogos;
        }
        return [
          ...prevLogos,
          {
            id: url,
            url,
            brandName,
          },
        ];
      });
      handleResetSearch();
    },
    [setLogos, handleResetSearch],
  );

  const handleShowAvailableLogos = useCallback((brand: BrandfetchBrand) => {
    setSelectedBrandForLogos(brand);
  }, []);

  const handleSearchTermChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      // Clear selected brand when user starts typing a new search
      if (selectedBrandForLogos) {
        setSelectedBrandForLogos(null);
      }
    },
    [selectedBrandForLogos],
  );

  const handleClearAllLogos = useCallback(() => {
    setLogos([]);
  }, [setLogos]);

  const handleDeleteLogo = useCallback((logoId: string) => {
    setLogos((prevLogos) => prevLogos.filter((logo) => logo.id !== logoId));
  }, []);

  // Check if there are any changes
  const hasChanges = useCallback(() => {
    const originalLogos = brandLogosBlock?.properties.logos || [];
    if (logos.length !== originalLogos.length) return true;

    return logos.some((logo, index) => {
      const originalLogo = originalLogos[index];
      return (
        !originalLogo ||
        logo.id !== originalLogo.id ||
        logo.url !== originalLogo.url ||
        logo.brandName !== originalLogo.brandName
      );
    });
  }, [logos, brandLogosBlock?.properties.logos]);

  return (
    <Modal.Instance
      // in most cases, these props are not needed as
      // <Modal.Instance><Modal.Trigger><MyButton /></Modal.Trigger><Modal.Root><MyContent /><Modal.Root></Modal.Instance>
      // is enough. this is an odd case where we are retrofitting in existing complex feature
      // please don't be influenced from this code when making your own modal feature. read storybook code.
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      <Modal.Root
        title="Brand experience"
        size="md"
        primaryAction={{
          label: 'Save',
          onClick: handleSave,
          disabled: !hasChanges(),
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: closeModal,
        }}
        tertiaryAction={{
          label: 'Clear all',
          onClick: handleClearAllLogos,
          disabled: logos.length === 0,
        }}
      >
        <>
          <div className="sticky top-0 z-10 bg-neutral-0 pb-4">
            <p className="mb-4 text-root-size-body-md leading-root-line-height-body-md text-root-text-color-base">
              Add logos of the brands you've worked with to highlight in this
              media kit.
            </p>
            <Popover.Instance
              open={
                (searchTerm.length > 1 && !selectedBrandForLogos) ||
                !!selectedBrandForLogos
              }
              onOpenChange={(nextValue) => {
                // Controlled by searchTerm and selectedBrandForLogos state
                if (!nextValue) {
                  handleResetSearch();
                }
              }}
            >
              <div className="relative w-full">
                <Input
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  placeholder="Enter a brand name"
                  className="h-8 w-full px-3 text-root-size-body-lg leading-root-line-height-body-lg"
                  aria-label="Enter a brand name"
                />
                <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center justify-center">
                  <Icon
                    name={searchTerm.length > 0 ? 'Close' : 'Search'}
                    color="neutral.300"
                    onClick={
                      searchTerm.length > 0 ? handleResetSearch : undefined
                    }
                    className={searchTerm.length > 0 ? 'cursor-pointer' : ''}
                  />
                </div>

                {/* Hidden anchor button for popover positioning, this hack is not representative
                 **  of real code and won't be needed when we migrate this component to use
                 ** AsyncAutocomplete
                 */}
                <Popover.Anchor className="w-full">
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute bottom-0 left-0 w-full opacity-0"
                    aria-hidden="true"
                  />
                </Popover.Anchor>

                <Popover.Root
                  sameWidth
                  size="hug"
                  gutter={4}
                  className="w-full"
                  portal
                  unmountOnHide
                  autoFocus={false}
                  autoFocusOnShow={false}
                  autoFocusOnHide={false}
                >
                  {/* SearchDropdown content - only show when searching and no brand selected */}
                  {!selectedBrandForLogos && searchTerm.length > 1 && (
                    <SearchDropdown
                      searchTerm={searchTerm}
                      brands={brands}
                      isLoading={isLoading}
                      onShowAvailableLogos={handleShowAvailableLogos}
                      onResetSearch={handleResetSearch}
                    />
                  )}

                  {/* AvailableLogos content - only show when a brand is selected */}
                  {selectedBrandForLogos && (
                    <AvailableLogos
                      brandName={selectedBrandForLogos.name}
                      brandDomain={selectedBrandForLogos.domain}
                      logos={logos}
                      onBrandSelect={handleBrandSelect}
                    />
                  )}
                </Popover.Root>
              </div>
            </Popover.Instance>
          </div>

          <div className="flex w-full flex-col gap-4">
            {logos.length > 0 && (
              <SelectedBrandLogos
                logos={logos}
                onDeleteLogo={handleDeleteLogo}
                onReorder={setLogos}
              />
            )}
            {logos.length === 0 && <StaticBrandLogos />}
          </div>
        </>
      </Modal.Root>
    </Modal.Instance>
  );
};
